import { useEffect, useRef, useState } from 'react';
import {
  ApiError,
  consentWrite,
  scribeActionDecide,
  scribeDiscard,
  scribeDraft,
  scribeResidents,
  scribeSign,
  scribeStart,
  scribeTranscript,
  type ApiClient,
  type ScribeSession,
  type ScribeSignResult,
} from '@aithora/shared';
import { ErrorNote } from '../../../ErrorNote';
import { ConfirmDialog } from '../../../ui/ConfirmDialog';
import { useQuery } from '../../../useQuery';
import { ACTION_ROUTE } from '../../atlas/scribeCommon';
import { A, sx } from '../parts';
import { PocDialog } from './PocDialog';
import { HEAD, box, prim } from './styles';

export interface SayPerson {
  id: string;
  name: string;
  room: string | null;
  consent: boolean;
}
export interface Segment {
  person: SayPerson;
  text: string;
}

const esc = (w: string) => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * Split what was said at each resident's name, so every part belongs to one person (the
 * phone's rule, apps/mobile/.../SayItOnceSheet.tsx splitByResident): "Edna took her meds,
 * Harold pulse 52" is Edna's part and Harold's part. A first name two residents share
 * counts only with the last name too. A part before any name joins the first named
 * resident. Nobody named: no parts, and the sheet asks who it was.
 */
export function splitByResident(text: string, people: SayPerson[]): Segment[] {
  const low = text.toLowerCase();
  const firsts = new Map<string, number>();
  for (const p of people) {
    const f = p.name.trim().split(/\s+/)[0]?.toLowerCase();
    if (f) firsts.set(f, (firsts.get(f) ?? 0) + 1);
  }
  const hits: { at: number; p: SayPerson }[] = [];
  for (const p of people) {
    const full = p.name.trim().toLowerCase();
    if (!full) continue;
    const first = full.split(/\s+/)[0];
    const words = firsts.get(first) === 1 ? [full, first] : [full];
    const taken: number[] = [];
    for (const w of words) {
      const re = new RegExp(`\\b${esc(w)}\\b`, 'g');
      let mt: RegExpExecArray | null;
      while ((mt = re.exec(low))) {
        const at = mt.index;
        if (taken.some((t) => at >= t && at < t + full.length)) continue;
        taken.push(at);
        hits.push({ at, p });
      }
    }
  }
  hits.sort((a, b) => a.at - b.at);
  const out: Segment[] = [];
  hits.forEach((h, i) => {
    const start = i === 0 ? 0 : h.at;
    const end = i + 1 < hits.length ? hits[i + 1].at : text.length;
    const part = text.slice(start, end).replace(/^[\s,;.]+|[\s,;]+$/g, '').trim();
    if (!part) return;
    const prev = out.find((o) => o.person.id === h.p.id);
    if (prev) prev.text = `${prev.text} ${part}`;
    else out.push({ person: h.p, text: part });
  });
  return out;
}

/** What was typed, one sentence per transcript line. */
const linesOf = (t: string) =>
  t
    .split(/(?<=[.!?])\s+/)
    .map((x) => x.trim())
    .filter(Boolean);

interface Drafted {
  person: SayPerson;
  text: string;
  session: ScribeSession | null;
  error: ApiError | null;
  noConsent: boolean;
}
const asApi = (e: unknown) => (e instanceof ApiError ? e : new ApiError({ kind: 'network', status: 0, message: 'Aithora did not answer.', method: 'POST', path: '' }));
const isNoConsent = (e: unknown) => e instanceof ApiError && e.status === 409 && (e.body as { reason?: string } | null)?.reason === 'no_recording_consent';
const first = (p: SayPerson) => p.name.split(/\s+/)[0];
const CARD = 'display:flex;flex-direction:column;gap:6px;padding:11px 12px;border-radius:10px;background:#FFFFFF;border:1px solid rgba(34,39,46,.12);';
const DEST = 'font-size:14px;font-weight:700;color:#264C7E;background:rgba(47,92,151,.10);border-radius:5px;padding:3px 7px;white-space:nowrap;';
const TICK = 'display:flex;align-items:flex-start;gap:10px;text-decoration:none;color:#22272E;text-align:left;width:100%;';

/**
 * Say it once (design pwLayer "say", README "Say it once"): one sentence about what you did,
 * split per resident so nothing lands on the wrong person, drafted by Atlas, read back, and
 * written only when you confirm. Each resident named gets their own Scribe note, through the
 * same routes the Scribe tab uses:
 *
 *   GET  /scribe/residents                 who a note can be about, with recording consent
 *   POST /scribe/sessions {resident_note}  one per resident (409 without recording consent)
 *   POST /scribe/sessions/:id/transcript   their part of the sentence, as typed lines
 *   POST /scribe/sessions/:id/draft        Atlas fills the note and proposes what else to do
 *   POST /scribe/sessions/:id/actions/:aid keep or drop, as ticked (a message to the nurse goes
 *                                          to the charge nurse on shift)
 *   POST /scribe/sessions/:id/sign         Confirm: the note goes on their chart and each kept
 *                                          step runs, sealed to the Truth Ledger
 *   POST /scribe/sessions/:id/discard      an unticked note, or closing without confirming
 *   POST /consent                          recording consent, when you record it here
 */
export function SayItOnceSheet({
  client,
  organizationId,
  charge,
  onClose,
  onToast,
}: {
  client: ApiClient;
  organizationId: string | null;
  charge: { userId: string; name: string | null } | null;
  onClose: () => void;
  onToast: (t: string) => void;
}) {
  const people = useQuery((signal) => scribeResidents(client, { signal }), [client]);
  const [text, setText] = useState('');
  const [drafts, setDrafts] = useState<Drafted[] | null>(null);
  const [ask, setAsk] = useState<string | null>(null);
  const [offNote, setOffNote] = useState<Record<string, boolean>>({});
  const [offAct, setOffAct] = useState<Record<string, boolean>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [notRun, setNotRun] = useState<string[] | null>(null);
  const [consentFor, setConsentFor] = useState<Drafted | null>(null);
  // Drafts still open on the server: discarded when the sheet closes without confirming.
  const live = useRef<string[]>([]);
  useEffect(
    () => () => {
      for (const id of live.current) void scribeDiscard(client, { params: { id } }).catch(() => undefined);
    },
    [client],
  );

  const list: SayPerson[] = people.data?.residents ?? [];
  const heard = !!drafts;
  const ready = (drafts ?? []).filter((d) => d.session && !offNote[d.session.id]);
  const keepCount = ready.reduce((n, d) => n + 1 + (d.session?.actions ?? []).filter((a) => !offAct[a.id]).length, 0);

  const discardAll = async () => {
    const ids = live.current;
    live.current = [];
    await Promise.all(ids.map((id) => scribeDiscard(client, { params: { id } }).catch(() => undefined)));
  };

  const draftOne = async (person: SayPerson, part: string): Promise<Drafted> => {
    try {
      const st = await scribeStart(client, { body: { noteType: 'resident_note', residentId: person.id } });
      live.current.push(st.session.id);
      const lines = linesOf(part);
      await scribeTranscript(client, { params: { id: st.session.id }, body: { lines: lines.length ? lines : [part], speaker: 'typed' } });
      const d = await scribeDraft(client, { params: { id: st.session.id } });      return { person, text: part, session: d.session, error: null, noConsent: false };
    } catch (e) {
      if (isNoConsent(e)) return { person, text: part, session: null, error: null, noConsent: true };
      return { person, text: part, session: null, error: asApi(e), noConsent: false };
    }
  };

  const readBack = async () => {
    const t = text.trim();
    if (!t || busy) return;
    setBusy(true);
    setError(null);
    setAsk(null);
    setNotRun(null);
    await discardAll();
    const parts = splitByResident(t, list);
    if (!parts.length) {
      setAsk(list.length ? 'Who was it? Say their name, for example the first name of one of your residents.' : 'No residents are assigned to you this shift, so there is no one to write about yet.');
      setBusy(false);
      return;
    }
    const out: Drafted[] = [];
    for (const p of parts) out.push(await draftOne(p.person, p.text));
    setDrafts(out);
    setOffNote({});
    setOffAct({});
    setBusy(false);
  };

  const recordConsent = async (d: Drafted) => {
    if (!organizationId) return;
    await consentWrite(client, { body: { userId: d.person.id, organizationId, consentType: 'scribe_recording', granted: true } });
    people.reload();
    const again = await draftOne(d.person, d.text);
    setDrafts((xs) => (xs ?? []).map((x) => (x.person.id === d.person.id ? again : x)));
  };

  const confirm = async () => {
    if (!drafts || !keepCount || busy) return;
    setBusy(true);
    setError(null);
    const names: string[] = [];
    const skipped: string[] = [];
    try {
      for (const d of drafts) {
        if (!d.session) continue;
        const id = d.session.id;
        if (offNote[id]) {
          await scribeDiscard(client, { params: { id } });
          live.current = live.current.filter((x) => x !== id);
          continue;
        }
        for (const a of d.session.actions) {
          const keep = !offAct[a.id];
          await scribeActionDecide(client, {
            params: { id, actionId: a.id },
            body: { decision: keep ? 'keep' : 'drop', ...(keep && a.kind === 'message_nurse' && charge ? { targetUserId: charge.userId } : {}) },
          });
        }
        const r: ScribeSignResult = await scribeSign(client, { params: { id } });
        live.current = live.current.filter((x) => x !== id);
        names.push(first(d.person));
        for (const x of r.actions) {
          if (x.outcome === 'executed' || x.outcome === 'dropped') continue;
          const a = d.session.actions.find((y) => y.id === x.id);
          skipped.push(`${first(d.person)}: ${a?.text ?? x.kind} (${x.reason ?? 'not done'})`);
        }
      }
      const said = `Saved for ${names.join(' and ')}. Every step is in the Truth Ledger.`;
      if (skipped.length) {
        setDrafts(null);
        setText('');
        setNotRun([said, ...skipped]);
      } else {
        onToast(said);
        onClose();
      }
    } catch (e) {
      setError(asApi(e));
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    void discardAll();
    onClose();
  };
  const goLabel = heard ? (keepCount ? `Confirm ${keepCount} ${keepCount === 1 ? 'entry' : 'entries'}` : 'Nothing to send') : 'Read it back';
  const goReady = !busy && (heard ? keepCount > 0 : !!text.trim() && !!people.data);

  return (
    <PocDialog
      title="Say it once"
      onClose={close}
      testId="poc-say"
      action={
        notRun ? undefined : (
          <A style={prim(goReady)} onClick={() => void (heard ? confirm() : readBack())} disabled={!goReady}>
            {goLabel}
          </A>
        )
      }
    >
      {people.error && <ErrorNote error={people.error} onRetry={people.reload} />}
      <textarea
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          setNotRun(null);
          if (drafts) {
            void discardAll();
            setDrafts(null);
          }
        }}
        disabled={busy}
        placeholder="Type what you did, with each person’s name, for example: Eleanor took her meds and ate half her dinner. Your computer’s dictation works here too."
        aria-label="What you did, in your words"
        style={sx('box-sizing:border-box;width:100%;min-height:76px;padding:10px 12px;border-radius:9px;border:1px solid rgba(34,39,46,.2);background:#FFFFFF;font-family:inherit;font-size:16px;line-height:1.45;color:#22272E;resize:vertical;')}
      />
      {!heard && !notRun && (
        <>
          <div style={sx(HEAD)}>How to say it</div>
          <span style={sx('font-size:16px;line-height:1.45;color:#3C454F;')}>
            Say each person’s name, then what you did. Several people in one go is fine: each one gets their own entries, so nothing lands on the wrong person.
          </span>
        </>
      )}
      {ask && (
        <div role="status" style={sx('padding:9px 11px;border-radius:9px;background:#FFFFFF;border:1px solid rgba(34,39,46,.14);font-size:16px;line-height:1.45;color:#3C454F;')}>
          {ask}
        </div>
      )}
      {(drafts ?? []).map((d) => {
        if (d.noConsent)
          return (
            <div key={d.person.id} data-testid={`say-consent-${d.person.id}`} style={sx(CARD + 'border-color:rgba(179,38,30,.35);')}>
              <span style={sx('font-size:16px;font-weight:700;color:#8A1C16;line-height:1.4;')}>{`${d.person.name} has no recording consent on file, so Atlas will not write a note about them.`}</span>
              <A
                style="align-self:flex-start;display:inline-flex;align-items:center;min-height:34px;padding:0 12px;border-radius:9px;background:#264C7E;color:#FFFFFF;font-size:16px;font-weight:700;text-decoration:none;"
                onClick={() => setConsentFor(d)}
                disabled={!organizationId}
              >
                Record consent
              </A>
            </div>
          );
        if (!d.session)
          return (
            <div key={d.person.id} role="alert" style={sx('padding:10px 12px;border-radius:10px;background:#FBEDEC;border:1px solid rgba(179,38,30,.4);font-size:16px;font-weight:700;color:#8A1C16;line-height:1.4;')}>
              {`${d.person.name}: ${d.error?.message ?? 'Atlas could not draft this part.'}`}
            </div>
          );
        const s = d.session;
        const noteOn = !offNote[s.id];
        const heardFields = s.fields.filter((f) => f.status !== 'NOT_HEARD' && f.value);
        return (
          <div key={s.id} data-testid={`say-note-${d.person.id}`} style={sx('display:flex;flex-direction:column;gap:8px;')}>
            <div style={sx(CARD)}>
              <button type="button" role="checkbox" aria-checked={noteOn} aria-label={`Note for ${d.person.name}`} className="wl-a" onClick={() => setOffNote((x) => ({ ...x, [s.id]: !x[s.id] }))} style={sx(TICK)}>
                <span style={sx(box(noteOn))}>{noteOn ? '✓' : ''}</span>
                <span style={sx('flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:2px;')}>
                  <span style={sx('font-size:16px;font-weight:800;line-height:1.3;')}>{`Note for ${d.person.name}`}</span>
                  {heardFields.length ? (
                    heardFields.map((f) => <span key={f.key} style={sx('font-size:16px;color:#3C454F;line-height:1.4;')}>{`${f.label}: ${f.value}`}</span>)
                  ) : (
                    <span style={sx('font-size:16px;color:#3C454F;line-height:1.4;')}>Atlas heard nothing to write for this part.</span>
                  )}
                </span>
              </button>
              <div style={sx('display:flex;flex-wrap:wrap;gap:5px;')}>
                <span style={sx(DEST)}>{`${first(d.person)}’s chart`}</span>
              </div>
            </div>
            {s.actions.map((a) => {
              const on = noteOn && !offAct[a.id];
              const nurseTo = a.kind === 'message_nurse' ? (charge ? (charge.name ? `Goes to ${charge.name}, the charge nurse on shift.` : 'Goes to the charge nurse on shift.') : 'No charge nurse is on shift, so this message has nobody to go to and will not be sent.') : null;
              return (
                <div key={a.id} data-testid={`say-act-${a.id}`} style={sx(CARD)}>
                  <button type="button" role="checkbox" aria-checked={on} aria-label={a.text} disabled={!noteOn} className="wl-a" onClick={() => setOffAct((x) => ({ ...x, [a.id]: !x[a.id] }))} style={sx(TICK)}>
                    <span style={sx(box(on))}>{on ? '✓' : ''}</span>
                    <span translate={a.kind === 'message_family' ? 'no' : undefined} style={sx('flex:1 1 auto;min-width:0;font-size:16px;font-weight:800;line-height:1.3;')}>{a.text}</span>
                  </button>
                  {a.kind === 'file_incident' && <span style={sx('font-size:16px;font-weight:700;color:#8A5A00;line-height:1.4;')}>Opens a Guardian case for the building. It stays open until the charge nurse closes it.</span>}
                  {a.kind === 'message_family' && <span style={sx('font-size:16px;font-weight:700;color:#8A5A00;line-height:1.4;')}>Goes to the family when you confirm. Check the words first.</span>}
                  {nurseTo && <span style={sx('font-size:16px;font-weight:700;color:#8A5A00;line-height:1.4;')}>{nurseTo}</span>}
                  <div style={sx('display:flex;flex-wrap:wrap;gap:5px;')}>
                    <span style={sx(DEST)}>{a.routeLabel ?? ACTION_ROUTE[a.kind] ?? a.kind}</span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      })}
      {error && <ErrorNote error={error} />}
      {error && <span style={sx('font-size:16px;font-weight:700;color:#8A5A00;line-height:1.4;')}>Anything already confirmed stays saved.</span>}
      {notRun && (
        <div role="status" data-testid="say-not-run" style={sx(CARD)}>
          <span style={sx('font-size:16px;font-weight:800;color:#22272E;')}>{notRun[0]}</span>
          <span style={sx('font-size:16px;font-weight:700;color:#8A5A00;')}>These steps did not run:</span>
          {notRun.slice(1).map((x) => (
            <span key={x} style={sx('font-size:16px;color:#3C454F;line-height:1.4;')}>
              {x}
            </span>
          ))}
        </div>
      )}
      {heard && (
        <div style={sx('display:flex;align-items:flex-start;gap:8px;padding:9px 11px;border-radius:9px;background:#FFFFFF;border:1px solid rgba(34,39,46,.14);')}>
          <span style={sx('flex:0 0 auto;font-size:14px;font-weight:800;letter-spacing:.08em;color:#8A5A00;border:1px solid rgba(138,90,0,.4);border-radius:4px;padding:1px 5px;margin-top:2px;')}>DRAFT</span>
          <span style={sx('font-size:16px;line-height:1.45;color:#3C454F;')}>
            Drafted by Atlas from your words. Nothing is written until you confirm. PointClickCare sync is waiting on partner approval, so entries are kept in Aithora until it is live.
          </span>
        </div>
      )}
      {consentFor && (
        <ConfirmDialog
          title={`Record recording consent for ${consentFor.person.name}?`}
          body="Only once they (or their representative) have agreed. It goes on their consent record under your name, and they can withdraw it at any time."
          confirmLabel="Record consent"
          onConfirm={() => recordConsent(consentFor)}
          onClose={() => setConsentFor(null)}
        />
      )}
    </PocDialog>
  );
}
